import { MAX_LEVEL } from "./constants";
import type {
  LevelObjectiveKind,
  LevelObjectiveState,
  ObjectiveReward,
  ObjectiveTargetShape,
  ObjectiveRewardGrant,
  ObjectiveRewardSpecial,
  ObjectiveTelemetry,
  PowerupKind
} from "./types";

interface LevelObjectivePlan {
  kind: LevelObjectiveKind;
  target: number;
  reward: ObjectiveReward;
}

interface Level20BountyPlan {
  kind: LevelObjectiveKind;
  baseTarget: number;
  scaling: "count" | "tiles";
}

const SCORE = (amount: number): ObjectiveRewardGrant => ({ type: "score", amount });
const DOOM = (amount: number): ObjectiveRewardGrant => ({ type: "doomSec", amount });
const CASH = (amount: number): ObjectiveRewardGrant => ({ type: "cash", amount });
const KEYS = (amount: number): ObjectiveRewardGrant => ({ type: "keys", amount });
const SPECIAL = (special: ObjectiveRewardSpecial, count = 1): ObjectiveRewardGrant => ({ type: "special", special, count });
const POWERUP = (powerup: PowerupKind, count = 1): ObjectiveRewardGrant => ({ type: "powerup", powerup, count });

const FIXED_LEVEL_OBJECTIVES: ReadonlyArray<LevelObjectivePlan> = [
  { kind: "LINE4_PLUS", target: 2, reward: { grants: [SCORE(4000), DOOM(2.0), SPECIAL("BOMB_3")] } },
  { kind: "CREATE_SPECIAL", target: 2, reward: { grants: [SCORE(4500), DOOM(2.0), CASH(50), SPECIAL("BOMB_3")] } },
  { kind: "BOMB_TRIGGER", target: 1, reward: { grants: [SCORE(5000), DOOM(2.25), CASH(75), SPECIAL("BOMB_3")] } },
  { kind: "SHAPE_MATCH", target: 1, reward: { grants: [SCORE(5500), DOOM(2.25), CASH(100), SPECIAL("BOMB_3")] } },
  { kind: "CHAIN_DEPTH_2_PLUS", target: 1, reward: { grants: [SCORE(6000), DOOM(2.25), CASH(125), KEYS(1)] } },
  { kind: "CLEAR_CRATES", target: 5, reward: { grants: [SCORE(6500), DOOM(2.5), CASH(150), SPECIAL("BOMB_3")] } },
  { kind: "WIPE_TRIGGER", target: 1, reward: { grants: [SCORE(7000), DOOM(2.5), CASH(175), POWERUP("HINT")] } },
  { kind: "CREATE_SPECIAL", target: 3, reward: { grants: [SCORE(7500), DOOM(2.5), CASH(200), SPECIAL("BOMB_4")] } },
  { kind: "BOMB_TRIGGER", target: 2, reward: { grants: [SCORE(8000), DOOM(2.75), CASH(225), KEYS(1)] } },
  { kind: "CLEAR_OBSTACLES", target: 7, reward: { grants: [SCORE(8500), DOOM(2.75), CASH(250), POWERUP("ROWRIP")] } },
  { kind: "LINE5_PLUS", target: 2, reward: { grants: [SCORE(9000), DOOM(3.0), CASH(275), SPECIAL("WIPE")] } },
  { kind: "SPECIAL_TRIGGER", target: 3, reward: { grants: [SCORE(9500), DOOM(3.0), CASH(300), SPECIAL("BOMB_4")] } },
  { kind: "WIPE_CLEAR_TILES", target: 16, reward: { grants: [SCORE(10000), DOOM(3.0), CASH(325), KEYS(1)] } },
  { kind: "CHAIN_DEPTH_3_PLUS", target: 1, reward: { grants: [SCORE(10500), DOOM(3.25), CASH(350), POWERUP("COLCUT")] } },
  { kind: "CLEAR_OBSTACLES", target: 10, reward: { grants: [SCORE(11000), DOOM(3.25), CASH(375), SPECIAL("WIPE")] } },
  { kind: "SPECIAL_CLEAR_TILES", target: 22, reward: { grants: [SCORE(11500), DOOM(3.25), CASH(425), SPECIAL("BOMB_5")] } },
  { kind: "LINE5_PLUS", target: 3, reward: { grants: [SCORE(12000), DOOM(3.5), CASH(475), KEYS(1), POWERUP("TRANSMUTE")] } },
  { kind: "CHAIN_DEPTH_3_PLUS", target: 2, reward: { grants: [SCORE(12500), DOOM(3.5), CASH(525), SPECIAL("WIPE")] } },
  { kind: "CHAIN_DEPTH_4_PLUS", target: 1, reward: { grants: [SCORE(13000), DOOM(3.5), CASH(600), SPECIAL("BOMB_5"), POWERUP("ZIGZAGBOLT")] } }
] as const;

const LEVEL20_BOUNTY_DEFS: ReadonlyArray<Level20BountyPlan> = [
  { kind: "BOMB_TRIGGER", baseTarget: 4, scaling: "count" },
  { kind: "CLEAR_OBSTACLES", baseTarget: 18, scaling: "count" },
  { kind: "SPECIAL_CLEAR_TILES", baseTarget: 30, scaling: "tiles" },
  { kind: "WIPE_CLEAR_TILES", baseTarget: 24, scaling: "tiles" },
  { kind: "LINE5_PLUS", baseTarget: 4, scaling: "count" },
  { kind: "CHAIN_DEPTH_4_PLUS", baseTarget: 2, scaling: "count" }
] as const;

const LEVEL20_POWERUP_CYCLE: ReadonlyArray<PowerupKind> = [
  "ROWRIP",
  "COLCUT",
  "TRANSMUTE",
  "ZIGZAGBOLT"
] as const;

const OBJECTIVE_LABELS: Readonly<Record<LevelObjectiveKind, string>> = {
  LINE4_PLUS: "CLEAR 4+ LINE BLOCKS",
  CREATE_SPECIAL: "CREATE SPECIAL BLOCKS",
  BOMB_TRIGGER: "TRIGGER BOMBS",
  SHAPE_MATCH: "MATCH SHAPES",
  CHAIN_DEPTH_2_PLUS: "GET 2+ COMBOS",
  CLEAR_CRATES: "BREAK CRATE BLOCKS",
  WIPE_TRIGGER: "TRIGGER WIPES",
  CLEAR_OBSTACLES: "CLEAR OBSTACLE BLOCKS",
  LINE5_PLUS: "CLEAR 5+ LINE BLOCKS",
  SPECIAL_TRIGGER: "TRIGGER SPECIALS",
  WIPE_CLEAR_TILES: "CLEAR BLOCKS WITH WIPE",
  CHAIN_DEPTH_3_PLUS: "GET 3+ COMBOS",
  SPECIAL_CLEAR_TILES: "CLEAR BLOCKS WITH SPECIAL",
  CHAIN_DEPTH_4_PLUS: "GET 4+ COMBOS"
} as const;

export const LEVEL20_BOUNTY_POOL: ReadonlyArray<LevelObjectiveKind> = LEVEL20_BOUNTY_DEFS.map((def) => def.kind);

function cloneReward(reward: ObjectiveReward): ObjectiveReward {
  return {
    grants: reward.grants.map((grant) => ({ ...grant }))
  };
}

function objectivePlanForLevel(level: number): LevelObjectivePlan | null {
  const wholeLevel = Math.max(1, Math.floor(level));
  if (wholeLevel >= MAX_LEVEL) {
    return null;
  }
  const clamped = Math.min(MAX_LEVEL - 1, wholeLevel);
  return FIXED_LEVEL_OBJECTIVES[clamped - 1] ?? null;
}

function level20BountyPlan(kind: LevelObjectiveKind): Level20BountyPlan {
  return LEVEL20_BOUNTY_DEFS.find((def) => def.kind === kind) ?? LEVEL20_BOUNTY_DEFS[0];
}

function compactScore(amount: number): string {
  if (amount >= 1000) {
    const compact = amount % 1000 === 0
      ? (amount / 1000).toString()
      : (amount / 1000).toFixed(1).replace(/\.0$/, "");
    return `${compact}K`;
  }
  return String(amount);
}

function rewardSpecialToken(special: ObjectiveRewardSpecial): string {
  if (special === "BOMB_3") {
    return "B3";
  }
  if (special === "BOMB_4") {
    return "B4";
  }
  if (special === "BOMB_5") {
    return "B5";
  }
  return "WIPE";
}

function rewardGrantToken(grant: ObjectiveRewardGrant): string {
  if (grant.type === "score") {
    return `+${compactScore(grant.amount)} SCORE`;
  }
  if (grant.type === "doomSec") {
    return `+${grant.amount.toFixed(1)}S`;
  }
  if (grant.type === "cash") {
    return `+$${grant.amount}`;
  }
  if (grant.type === "keys") {
    return `+${grant.amount} KEY${grant.amount === 1 ? "" : "S"}`;
  }
  if (grant.type === "special") {
    return grant.count === 1
      ? `+${rewardSpecialToken(grant.special)}`
      : `+${grant.count} ${rewardSpecialToken(grant.special)}`;
  }
  return grant.count === 1
    ? `+${grant.powerup}`
    : `+${grant.count} ${grant.powerup}`;
}

export function objectiveLabel(kind: LevelObjectiveKind): string {
  return OBJECTIVE_LABELS[kind];
}

export function level20BountyTier(level20BountyCompletions: number): number {
  return Math.max(0, Math.floor(Math.max(0, Math.floor(level20BountyCompletions)) / 4));
}

export function level20BountyDefinition(
  kind: LevelObjectiveKind,
  level20BountyCompletions = 0
): { kind: LevelObjectiveKind; target: number } {
  const def = level20BountyPlan(kind);
  const tier = level20BountyTier(level20BountyCompletions);
  const target = def.baseTarget + (def.scaling === "tiles" ? tier * 6 : tier);
  return {
    kind: def.kind,
    target
  };
}

export function objectiveRewardForLevel(level: number, level20BountyCompletions = 0): ObjectiveReward {
  const wholeLevel = Math.max(1, Math.floor(level));
  if (wholeLevel >= MAX_LEVEL) {
    const tier = level20BountyTier(level20BountyCompletions);
    const powerup = LEVEL20_POWERUP_CYCLE[Math.max(0, Math.floor(level20BountyCompletions)) % LEVEL20_POWERUP_CYCLE.length] ?? "ROWRIP";
    return {
      grants: [
        SCORE(14000 + tier * 1000),
        DOOM(3.75 + tier * 0.25),
        CASH(700 + tier * 75),
        KEYS(1 + (tier % 2)),
        SPECIAL("WIPE"),
        SPECIAL("BOMB_5"),
        POWERUP(powerup)
      ]
    };
  }
  const plan = objectivePlanForLevel(wholeLevel) ?? FIXED_LEVEL_OBJECTIVES[0];
  return cloneReward(plan.reward);
}

const SHAPE_MATCH_SHAPES: ObjectiveTargetShape[] = ["SQUARE", "L", "T"];

export function createObjectiveState(kind: LevelObjectiveKind, target: number): LevelObjectiveState {
  const normalizedTarget = Math.max(1, Math.floor(target));
  const state: LevelObjectiveState = {
    kind,
    progress: 0,
    target: normalizedTarget,
    completed: false,
    rewarded: false
  };
  if (kind === "SHAPE_MATCH") {
    state.targetShape = SHAPE_MATCH_SHAPES[Math.floor(Math.random() * SHAPE_MATCH_SHAPES.length)];
  }
  return state;
}

export function createObjectiveStateForLevel(level: number): LevelObjectiveState | null {
  const plan = objectivePlanForLevel(level);
  return plan == null ? null : createObjectiveState(plan.kind, plan.target);
}

export function createLevel20BountyState(
  kind: LevelObjectiveKind,
  level20BountyCompletions = 0
): LevelObjectiveState {
  const plan = level20BountyDefinition(kind, level20BountyCompletions);
  return createObjectiveState(plan.kind, plan.target);
}

export function normalizeObjectiveState(
  kind: LevelObjectiveKind,
  target: number,
  state: Partial<LevelObjectiveState> | null | undefined
): LevelObjectiveState {
  const normalizedTarget = Math.max(1, Math.floor(target));
  const progress = Math.max(0, Math.min(normalizedTarget, Math.floor(state?.progress ?? 0)));
  const completed = state?.completed === true || progress >= normalizedTarget;
  return {
    kind,
    progress,
    target: normalizedTarget,
    completed,
    rewarded: completed && state?.rewarded === true
  };
}

export function emptyObjectiveTelemetry(): ObjectiveTelemetry {
  return {
    line4PlusMatches: 0,
    line5PlusMatches: 0,
    shapeMatches: 0,
    squareMatches: 0,
    lMatches: 0,
    tMatches: 0,
    specialsCreated: 0,
    bombTriggers: 0,
    wipeTriggers: 0,
    specialTriggers: 0,
    crateBreaks: 0,
    obstacleClears: 0,
    specialClearTiles: 0,
    wipeClearTiles: 0,
    chainDepth2PlusResolves: 0,
    chainDepth3PlusResolves: 0,
    chainDepth4PlusResolves: 0
  };
}

export function objectiveProgressFromTelemetry(kind: LevelObjectiveKind, telemetry: ObjectiveTelemetry, targetShape?: ObjectiveTargetShape): number {
  if (kind === "LINE4_PLUS") {
    return telemetry.line4PlusMatches;
  }
  if (kind === "CREATE_SPECIAL") {
    return telemetry.specialsCreated;
  }
  if (kind === "BOMB_TRIGGER") {
    return telemetry.bombTriggers;
  }
  if (kind === "SHAPE_MATCH") {
    if (targetShape === "SQUARE") return telemetry.squareMatches;
    if (targetShape === "L") return telemetry.lMatches;
    if (targetShape === "T") return telemetry.tMatches;
    return telemetry.shapeMatches;
  }
  if (kind === "CHAIN_DEPTH_2_PLUS") {
    return telemetry.chainDepth2PlusResolves;
  }
  if (kind === "CLEAR_CRATES") {
    return telemetry.crateBreaks;
  }
  if (kind === "WIPE_TRIGGER") {
    return telemetry.wipeTriggers;
  }
  if (kind === "CLEAR_OBSTACLES") {
    return telemetry.obstacleClears;
  }
  if (kind === "LINE5_PLUS") {
    return telemetry.line5PlusMatches;
  }
  if (kind === "SPECIAL_TRIGGER") {
    return telemetry.specialTriggers;
  }
  if (kind === "WIPE_CLEAR_TILES") {
    return telemetry.wipeClearTiles;
  }
  if (kind === "CHAIN_DEPTH_3_PLUS") {
    return telemetry.chainDepth3PlusResolves;
  }
  if (kind === "SPECIAL_CLEAR_TILES") {
    return telemetry.specialClearTiles;
  }
  if (kind === "CHAIN_DEPTH_4_PLUS") {
    return telemetry.chainDepth4PlusResolves;
  }
  return 0;
}

export function objectiveRewardTokens(reward: ObjectiveReward): string[] {
  return reward.grants.map(rewardGrantToken);
}

export function objectiveRewardSummary(reward: ObjectiveReward): string {
  return objectiveRewardTokens(reward).join(" ");
}
